import {
  BookOpenText,
  Bot,
  ChartColumnIncreasing,
  CircleDollarSign,
  CodeXml,
  Database,
  House,
  LifeBuoy,
  Map,
  Newspaper,
  NotebookText,
  Scale,
  Settings,
  Shield,
  SquareScissors,
  WandSparkles,
} from "lucide-react";

import { NavMain, type NavMainItem } from "@/components/side-bar/nav-main";
import {
  NavSecondary,
  type NavSecondaryItem,
} from "@/components/side-bar/nav-secondary";
import { NavUser } from "@/components/side-bar/nav-user";
import { OrgSwitcher } from "@/components/side-bar/org-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar";

import {
  isAdminIdentity,
  isSalesIdentity,
  useIdentity,
} from "contexts/identity-context";
import * as Routes from "routes";

interface NavMainItemWithAccess extends NavMainItem {
  adminOnly?: boolean;
  salesOnly?: boolean;
}

const navMain: NavMainItemWithAccess[] = [
  {
    title: "Home",
    url: Routes.hub,
    icon: House,
  },
  {
    title: "Explorer",
    url: Routes.dataExplorer,
    icon: Map,
  },
  {
    title: "Clip",
    url: "/clip",
    icon: SquareScissors,
    items: [
      { title: "Clip UI", url: Routes.clipUi },
      { title: "Clip Management", url: Routes.clipManagement },
    ],
  },
  {
    title: "Catalogue",
    url: Routes.dataCatalogue,
    icon: BookOpenText,
  },
  {
    title: "Datasets",
    url: "/datasets",
    icon: Database,
    items: [
      { title: "Standard", url: Routes.dataDownload },
      { title: "My Custom", url: Routes.dataCustomDownload },
    ],
  },
  {
    title: "Batch",
    url: Routes.batchBatches,
    icon: Bot,
  },
  {
    title: "Apps",
    url: Routes.developerApiKeys,
    icon: CodeXml,
  },
  {
    title: "Demos",
    url: Routes.demosPage,
    icon: WandSparkles,
  },
  {
    title: "Documentation",
    url: Routes.docsPage,
    icon: NotebookText,
  },
  {
    title: "Usage",
    url: Routes.developerUsage,
    icon: ChartColumnIncreasing,
  },
  {
    title: "Settings",
    url: "/settings",
    icon: Settings,
    items: [
      { title: "General", url: Routes.orgGeneralSettings },
      { title: "Pending Invitations", url: Routes.userPendingInvitations },
      { title: "Members", url: Routes.orgMembers },
      { title: "Subscription", url: Routes.orgSubscription },
      { title: "Billing", url: Routes.orgBillingDetails },
    ],
  },
  {
    title: "Admin",
    url: "/admin",
    icon: Shield,
    adminOnly: true,
    items: [
      { title: "Organisations", url: Routes.adminOrganisations },
      { title: "Demos", url: Routes.adminDemoManagement },
      { title: "Users", url: Routes.adminUsers },
      { title: "Datasets", url: Routes.adminDatasets },
      { title: "Custom Datasets", url: Routes.adminViewCustomDatasets },
      { title: "Releases", url: Routes.adminReleases },
      { title: "Clip Reporting", url: Routes.adminClipReporting },
    ],
  },
  {
    title: "Sales Tools",
    url: "/sales-tools",
    icon: CircleDollarSign,
    salesOnly: true,
    items: [
      { title: "Building Updates", url: Routes.salesToolsRuralSchedule },
      { title: "Opportunities", url: Routes.salesToolsOpportunities },
    ],
  },
];

const navSecondary: NavSecondaryItem[] = [
  { name: "What's New", url: Routes.whatsNew, icon: Newspaper },
  {
    name: "Help",
    url: "https://geoscape.com.au/support/",
    icon: LifeBuoy,
    target: "_blank",
  },
  { name: "Legal", url: Routes.legal, icon: Scale },
];

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const [identityState] = useIdentity();

  const visibleNavItems = navMain.filter((item) => {
    if (item.adminOnly) return isAdminIdentity(identityState);
    if (item.salesOnly) return isSalesIdentity(identityState);
    return true;
  });

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <OrgSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={visibleNavItems} />
      </SidebarContent>
      <SidebarFooter>
        <NavSecondary items={navSecondary} />
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
